'use client';

import React, { TransitionEvent, useEffect, useRef, useState } from 'react';

import Box, { BoxProps } from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useTheme, duration } from '@mui/material/styles';

import { CollapseToggle } from './collapseToggle';

interface Props extends Omit<BoxProps, 'width' | 'minWidth'> {
  anchor?: 'left' | 'right';
  collapsible?: boolean;
  minWidth?: number;
  onWidthChange?: (width: number) => void;
  resizable?: boolean;
  width?: number;
}

export function CollapsibleResizableContainer({
  anchor = 'left',
  children,
  collapsible,
  onWidthChange,
  resizable,
  ...props
}: Props) {
  const { transitions } = useTheme();
  const [width, setWidth] = useState<number | undefined>(undefined); // Initial width of the container
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [applyMinWidth, setApplyMinWidth] = useState(false);
  const disableTransition = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) {
      return;
    }

    const startX = e.clientX;
    const startWidth = width ?? containerRef.current?.clientWidth;
    disableTransition.current = true;

    const onMouseMove = (e: MouseEvent) => {
      const delta = anchor === 'left' ? e.clientX - startX : startX - e.clientX;
      const newWidth = startWidth + delta;
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      disableTransition.current = false;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
  };

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'width') {
      return;
    }

    if (!isCollapsed) {
      setApplyMinWidth(true);
    }
  };

  useEffect(() => {
    if (isCollapsed) {
      setApplyMinWidth(false);
    }
  }, [isCollapsed]);

  const parsedWidth = Math.max(width ?? props.width ?? 0, props.minWidth ?? 0);
  const finalWidth = isCollapsed ? 24 : parsedWidth;

  useEffect(() => {
    onWidthChange?.(finalWidth);
  }, [finalWidth, onWidthChange]);

  return (
    <Box
      ref={containerRef}
      position="relative"
      {...props}
      data-testid="collapsible-resizable-container"
      minWidth={applyMinWidth ? (isCollapsed ? 24 : props.minWidth) : undefined}
      onTransitionEnd={handleTransitionEnd}
      sx={{
        ...props.sx,
        transition: disableTransition.current
          ? undefined
          : `width ${duration.standard}ms ${transitions.easing.easeInOut}`,
        width: `${finalWidth}px !important`,
        [`.${CollapseToggle.className}`]: {
          opacity: 0,
        },
        [`&:hover .${CollapseToggle.className}`]: {
          opacity: 1,
        },
      }}
    >
      <Stack flex="1 1 auto" minHeight={0} overflow="hidden">
        <Stack
          flex="1 1 auto"
          maxWidth={'100%'}
          minHeight={0}
          overflow="visible"
          width={parsedWidth}
        >
          {children}
        </Stack>
      </Stack>
      {resizable && (
        <Box
          bottom={0}
          data-resize-handle="true"
          height="100%"
          onMouseDown={handleMouseDown}
          position="absolute"
          sx={{
            cursor: 'ew-resize',
            ...(anchor === 'left' ? { right: -5 } : { left: -5 }),
          }}
          title="Resize"
          top={0}
          width={10}
          zIndex={1}
        />
      )}
      {collapsible && (
        <CollapseToggle
          anchor={anchor}
          collapsed={isCollapsed}
          onClick={() => setIsCollapsed(!isCollapsed)}
        />
      )}
    </Box>
  );
}
